// Firebase（ログインと、個人情報・取引先の情報の Firestore）。
// 事務所 Web は Google ＋ 2 段階目（TOTP）が必須（仕様書「事務所 Web の 2 段階認証」）。
// 手元とテストはエミュレーターにつなぐ。Auth のエミュレーターは TOTP に対応していないため、手元では
// 「Google ＋ 6 桁でログインしたことにする」署名なしのトークンを作る（API と Firestore のエミュレーターだけが受け付ける）
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  GoogleAuthProvider, TotpMultiFactorGenerator, getAuth, getMultiFactorResolver, multiFactor, onAuthStateChanged, reauthenticateWithPopup,
  signInWithPopup, signOut, type MultiFactorError, type MultiFactorResolver, type TotpSecret, type User,
} from 'firebase/auth';
import { connectFirestoreEmulator, getFirestore, type Firestore } from 'firebase/firestore';

export const isEmulator = import.meta.env.VITE_FIREBASE_EMULATOR === '1';
const PROJECT = isEmulator ? 'demo-torapon' : (import.meta.env.VITE_FIREBASE_PROJECT_ID as string | undefined);
const cfg = isEmulator
  ? { apiKey: 'demo-key', authDomain: 'localhost', projectId: 'demo-torapon' }
  : { apiKey: import.meta.env.VITE_FIREBASE_API_KEY as string | undefined, authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN as string | undefined, projectId: PROJECT };
export const firebaseReady = Boolean(cfg.apiKey && cfg.projectId);

const app = initializeApp(firebaseReady ? cfg : { apiKey: 'missing', projectId: 'missing' });
const auth = getAuth(app);

/** いまのログインの状態 */
export type Session =
  | { state: 'out' }
  /** Google でログインしたが、2 段階目の登録がまだ */
  | { state: 'enroll'; uid: string; email: string }
  | { state: 'in'; uid: string; email: string };

let session: Session = { state: 'out' };
const listeners = new Set<(s: Session) => void>();
const emit = (s: Session) => { session = s; listeners.forEach((f) => f(s)); };
export const onSession = (f: (s: Session) => void) => { listeners.add(f); f(session); return () => { listeners.delete(f); }; };

// ===== Firestore（エミュレーターではログインごとに作り直す：トークンを差し替えるため） =====
let db: Firestore | null = isEmulator ? null : getFirestore(app);
let dbVersion = 0;
/** いまの Firestore。ログインし直したら別のものになる（useDb で受け取る） */
export const fsdb = () => { if (!db) throw new Error('ログインしていません'); return db; };
export const fsVersion = () => dbVersion;

// ===== エミュレーター（手元・テスト） =====
type Mock = { uid: string; email: string; authTime: number };
const MOCK_KEY = 'tp_mock_session';
const b64 = (o: unknown) => btoa(unescape(encodeURIComponent(JSON.stringify(o)))).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
const mockClaims = (m: Mock) => ({
  sub: m.uid, user_id: m.uid, email: m.email, email_verified: true, auth_time: m.authTime,
  firebase: { sign_in_provider: 'google.com' as const, sign_in_second_factor: 'totp', identities: {} },
});
let mock: Mock | null = null;
let mockApp: FirebaseApp | null = null;
function openMock(m: Mock) {
  mock = m;
  try { sessionStorage.setItem(MOCK_KEY, JSON.stringify(m)); } catch { /* 保存できなくても動く */ }
  mockApp = initializeApp(cfg, `mock-${Date.now()}`);
  db = getFirestore(mockApp);
  connectFirestoreEmulator(db, '127.0.0.1', 8080, { mockUserToken: mockClaims(m) });
  dbVersion++;
  emit({ state: 'in', uid: m.uid, email: m.email });
}
/** 手元だけ：Google ＋ 6 桁でログインしたことにする */
export function signInEmulator(email: string) {
  const e = email.trim().toLowerCase();
  openMock({ uid: `uid-${e.replace(/[^a-z0-9]/g, '-')}`, email: e, authTime: Math.floor(Date.now() / 1000) });
}

// ===== 本番：Google → 2 段階目 =====
/** Google でログイン。2 段階目が要るときは resolver を返す（画面で 6 桁を入れて resolveSecondFactor） */
export async function signInGoogle(): Promise<MultiFactorResolver | null> {
  try { await signInWithPopup(auth, new GoogleAuthProvider()); return null; } catch (e) { return mfaResolver(e); }
}
function mfaResolver(e: unknown): MultiFactorResolver {
  if ((e as { code?: string }).code === 'auth/multi-factor-auth-required') return getMultiFactorResolver(auth, e as MultiFactorError);
  throw e;
}
export async function resolveSecondFactor(resolver: MultiFactorResolver, code: string) {
  const hint = resolver.hints.find((h) => h.factorId === TotpMultiFactorGenerator.FACTOR_ID);
  if (!hint) throw new Error('認証アプリが登録されていません');
  await resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(hint.uid, code));
}
/** 2 段階目の登録：QR コードの元になる秘密を作る */
export async function startEnroll(): Promise<TotpSecret> {
  return TotpMultiFactorGenerator.generateSecret(await multiFactor(auth.currentUser!).getSession());
}
export async function finishEnroll(secret: TotpSecret, code: string) {
  await multiFactor(auth.currentUser!).enroll(TotpMultiFactorGenerator.assertionForEnrollment(secret, code), '認証アプリ');
  // 2 段階目を通ったトークンにするため、いったんログアウトしてログインし直してもらう
  await signOut(auth);
}

async function realState(u: User | null): Promise<Session> {
  if (!u?.email) return { state: 'out' };
  const t = await u.getIdTokenResult();
  const second = (t.claims.firebase as { sign_in_second_factor?: string } | undefined)?.sign_in_second_factor;
  return second === 'totp' ? { state: 'in', uid: u.uid, email: u.email.toLowerCase() } : { state: 'enroll', uid: u.uid, email: u.email.toLowerCase() };
}
if (isEmulator) {
  try { const saved = sessionStorage.getItem(MOCK_KEY); if (saved) openMock(JSON.parse(saved) as Mock); } catch { /* なければログイン画面 */ }
  if (!mock) emit({ state: 'out' });
} else {
  onAuthStateChanged(auth, async (u) => emit(await realState(u)));
}

/** API に付ける ID トークン */
export async function idToken(): Promise<string | null> {
  if (isEmulator) {
    if (!mock) return null;
    const now = Math.floor(Date.now() / 1000);
    return `${b64({ alg: 'none', typ: 'JWT' })}.${b64({ ...mockClaims(mock), iss: `https://securetoken.google.com/${PROJECT}`, aud: PROJECT, iat: now, exp: now + 3600 })}.`;
  }
  return (await auth.currentUser?.getIdToken()) ?? null;
}

/** 最後に 6 桁を入れた時刻（秒） */
export async function authTime(): Promise<number> {
  if (isEmulator) return mock?.authTime ?? 0;
  const t = await auth.currentUser?.getIdTokenResult();
  return t ? Number(t.claims.auth_time ?? 0) : 0;
}

/** 大事な操作の前の再確認：Google をもう一度通し、6 桁を入れる。本番は resolver を返す */
export async function startStepUp(): Promise<MultiFactorResolver | null> {
  if (isEmulator) return null;
  try { await reauthenticateWithPopup(auth.currentUser!, new GoogleAuthProvider()); return null; } catch (e) { return mfaResolver(e); }
}
/** 再確認の 6 桁（エミュレーターは 6 桁なら通す） */
export async function finishStepUp(resolver: MultiFactorResolver | null, code: string) {
  if (isEmulator) { if (!/^\d{6}$/.test(code)) throw new Error('6 桁の数字を入れてください'); openMock({ ...mock!, authTime: Math.floor(Date.now() / 1000) }); return; }
  await resolveSecondFactor(resolver!, code);
  await auth.currentUser!.getIdToken(true);
}

export async function logout() {
  if (isEmulator) { mock = null; try { sessionStorage.removeItem(MOCK_KEY); } catch { /* なし */ } db = null; emit({ state: 'out' }); return; }
  await signOut(auth);
}
